import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Play, Download, HelpCircle, ListChecks, Layers, Zap, X } from "lucide-react";
import api from "@/lib/api";
import { Logo, WordMark } from "@/components/brand";import { usePWAInstall } from "@/hooks/usePWAInstall";

export default function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ questions: null, categories: null });
  const { canInstall, isStandalone, promptInstall } = usePWAInstall();
  const [showInstall, setShowInstall] = useState(true);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="relative min-h-screen bg-dh-black dh-grain overflow-hidden">
      {/* Ambient red glow */}
      <div className="pointer-events-none absolute -top-32 -right-24 h-80 w-80 rounded-full bg-dh-red opacity-20 blur-[100px]" />
      <div className="pointer-events-none absolute top-40 -left-24 h-72 w-72 rounded-full bg-dh-blue opacity-10 blur-[100px]" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-lg flex-col px-5 pb-10 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between animate-fade-up">
          <img src="/logo-transparent.png?v=4" alt="Douzième Homme" className="h-16 w-auto" data-testid="home-logo" />
          <span className="rounded-full border border-dh-borderDark bg-dh-cardDark px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white/70">
            Partenaire OGC Nice
          </span>
        </div>

        {/* Hero */}
        <div className="mt-10 flex flex-col items-start animate-fade-up delay-1">
          <span className="mb-4 inline-block rounded-full bg-dh-red/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-dh-red">
            App compagnon officielle
          </span>
          <WordMark className="text-5xl sm:text-6xl font-extrabold tracking-tight" />
          <p className="mt-5 max-w-sm text-base leading-relaxed text-white/60">
            Le maître du jeu pour votre plateau physique <span className="text-white/90 font-semibold">Édition Nice</span>. Posez les questions, gérez les tours, laissez la ferveur monter autour de la table.
          </p>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 gap-3 animate-fade-up delay-2">
          <StatCard
            testid="stat-questions"
            icon={<ListChecks className="h-5 w-5" />}
            value={stats.questions}
            label="Questions"
          />
          <StatCard
            testid="stat-categories"
            icon={<Layers className="h-5 w-5" />}
            value={stats.categories}
            label="Catégories"
          />
        </div>

        {/* Main CTA */}
        <button
          data-testid="btn-start-game"
          onClick={() => navigate("/jouer")}
          className="dh-red-glow mt-8 flex h-16 w-full items-center justify-center gap-3 rounded-2xl bg-dh-red text-lg font-display font-extrabold uppercase tracking-wider text-white transition-transform active:scale-[0.98] hover:bg-dh-redHover animate-fade-up delay-3"
        >
          <Play className="h-6 w-6 fill-white" />
          Commencer une partie
        </button>

        {/* How it works */}
        <div className="mt-10 animate-fade-up delay-4">
          <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/40">
            <HelpCircle className="h-4 w-4" /> Le principe
          </div>
          <div className="space-y-3">
            <PrincipleRow n="1" title="Installez-vous autour du plateau" text="Le jeu physique reste maître : pions, cases et déplacements se jouent sur la table." />
            <PrincipleRow n="2" title="L'app pose les questions" text="Choisissez une catégorie, l'app tire une question. Bonne réponse : vous rejouez. Sinon, au suivant." />
            <PrincipleRow n="3" title="Deux modes de jeu" text="Classique avec 4 réponses, ou Expert avec chrono de 20 secondes et réponse à l'oral." />
          </div>
        </div>

        <div className="flex-1" />

        <div className="mt-8 flex items-center justify-center gap-6 text-xs text-white/30 animate-fade-up delay-4">
          <span>Conçu à Nice · Fabriqué en France</span>
          <Link to="/admin" data-testid="footer-admin-link" className="underline underline-offset-2 hover:text-white/60">
            Espace admin
          </Link>
        </div>
      </div>

      {/* PWA install banner */}
      {canInstall && !isStandalone && showInstall && (
        <div className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-lg p-4 animate-fade-up">
          <div className="flex items-center gap-3 rounded-2xl border border-dh-borderDark bg-dh-cardDark/95 p-3 backdrop-blur">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-dh-red/20 text-dh-red">
              <Zap className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-white">Installez l'application</p>
              <p className="truncate text-xs text-white/50">Accès rapide depuis votre écran d'accueil</p>
            </div>
            <button
              data-testid="btn-pwa-install"
              onClick={promptInstall}
              className="flex items-center gap-1.5 rounded-xl bg-dh-red px-3 py-2 text-sm font-bold text-white active:scale-95"
            >
              <Download className="h-4 w-4" /> Installer
            </button>
            <button onClick={() => setShowInstall(false)} className="p-1 text-white/40" aria-label="Fermer">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, value, label, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-dh-borderDark bg-dh-cardDark p-4">
      <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-dh-red/15 text-dh-red">{icon}</div>
      <div className="font-display text-3xl font-extrabold text-white">
        {value === null ? "—" : value.toLocaleString("fr-FR")}
      </div>
      <div className="text-xs font-semibold uppercase tracking-wider text-white/40">{label}</div>
    </div>
  );
}

function PrincipleRow({ n, title, text }) {
  return (
    <div className="flex gap-3 rounded-xl border border-dh-borderDark/60 bg-dh-cardDark/50 p-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-dh-red font-display text-sm font-extrabold text-white">
        {n}
      </div>
      <div>
        <p className="text-sm font-semibold text-white">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-white/50">{text}</p>
      </div>
    </div>
  );
}
